import { useMemo } from "react";
import dayjs from "dayjs";
import { calendarWeekLanes } from "../utils/calendarWeekLanes.js";
import { academyDateKey } from "../utils/date.js";

const weekdays = ["일", "월", "화", "수", "목", "금", "토"];

function isInRange(dateKey, range) {
  return dateKey >= dayjs(range.start).format("YYYY-MM-DD") && dateKey <= dayjs(range.end).format("YYYY-MM-DD");
}

function getRangePosition(current, range) {
  const dateKey = current.format("YYYY-MM-DD");
  const starts = dateKey === dayjs(range.start).format("YYYY-MM-DD") || current.day() === 0;
  const ends = dateKey === dayjs(range.end).format("YYYY-MM-DD") || current.day() === 6;
  if (starts && ends) return "single";
  if (starts) return "start";
  if (ends) return "end";
  return "middle";
}

export function MonthlyCalendarGrid({
  month,
  ranges = [],
  selectedDate = "",
  isDateHighlighted,
  onDateSelect,
  onDateActivate,
  onDateMouseDown,
  onDateMouseEnter,
  onMouseUp,
  ariaItemLabel = "일정",
  maxVisibleRanges = Infinity,
  className = ""
}) {
  const days = useMemo(() => {
    const start = month.startOf("month").startOf("week");
    return Array.from({ length: 42 }, (_, index) => start.add(index, "day"));
  }, [month]);
  const weeks = useMemo(() => Number.isFinite(maxVisibleRanges)
    ? Array.from({ length: 6 }, (_, index) => calendarWeekLanes(ranges, days[index * 7].format("YYYY-MM-DD"), days[index * 7 + 6].format("YYYY-MM-DD")))
    : null, [days, ranges, maxVisibleRanges]);

  const today = academyDateKey();
  return (
    <div className={`monthly-calendar-scroll ${className}`.trim()} onMouseUp={onMouseUp}>
      <div className="monthly-calendar-grid">
        {weekdays.map((weekday, index) => (
          <div
            key={weekday}
            className={["monthly-calendar-weekday", index === 0 && "is-sunday", index === 6 && "is-saturday"].filter(Boolean).join(" ")}
          >
            {weekday}
          </div>
        ))}

        {days.map((current, index) => {
          const dateKey = current.format("YYYY-MM-DD");
          const currentRanges = ranges.filter((range) => isInRange(dateKey, range));
          const slots = weeks ? weeks[Math.floor(index / 7)].map((lane) => lane.find((range) => isInRange(dateKey, range))) : currentRanges;
          const hiddenCount = slots.slice(maxVisibleRanges).filter(Boolean).length;
          const isOutside = !current.isSame(month, "month");
          const isToday = dateKey === today;
          const isSelected = dateKey === selectedDate || Boolean(isDateHighlighted?.(dateKey, current));
          const weekday = current.day();
          const classNames = [
            "monthly-calendar-cell",
            isOutside && "is-outside",
            isSelected && "is-selected",
            isToday && "is-today",
            weekday === 0 && "is-sunday",
            weekday === 6 && "is-saturday",
            weekday === 6 && "is-last-column",
            index >= 35 && "is-last-row"
          ].filter(Boolean).join(" ");

          return (
            <button
              key={dateKey}
              type="button"
              className={classNames}
              aria-label={`${current.format("YYYY년 M월 D일")}${currentRanges.length ? `, ${ariaItemLabel} ${currentRanges.length}건` : ""}${hiddenCount ? `, ${hiddenCount}개 더 보기` : ""}`}
              aria-pressed={isSelected}
              onClick={() => onDateSelect?.(dateKey, current, isOutside)}
              onKeyDown={(event) => {
                if (!onDateActivate || (event.key !== "Enter" && event.key !== " ")) return;
                event.preventDefault();
                onDateActivate(dateKey, current);
              }}
              onMouseDown={(event) => onDateMouseDown?.(dateKey, current, event)}
              onMouseEnter={(event) => onDateMouseEnter?.(dateKey, current, event)}
            >
              <span className="monthly-calendar-date">{current.date()}</span>
              <span className="monthly-calendar-cell-content">
                <span className="monthly-calendar-ranges">
                  {slots.slice(0, maxVisibleRanges).map((range, slot) => {
                    if (!range) return <span key={`empty-${slot}`} className="monthly-calendar-range-slot" aria-hidden="true" />;
                    const position = getRangePosition(current, range);
                    const label = range.getLabel
                      ? range.getLabel(dateKey, position)
                      : position === "start" || position === "single"
                        ? range.label
                        : "";
                    const title = range.getTitle ? range.getTitle(dateKey) : range.title || range.label;

                    return (
                      <span
                        key={range.key}
                        className={`monthly-calendar-range is-${position}`}
                        style={{ "--calendar-range-color": range.color || "#1677ff" }}
                        title={title}
                      >
                        {label}
                      </span>
                    );
                  })}
                  {hiddenCount > 0 && <span className="monthly-calendar-more">+{hiddenCount}개 더 보기</span>}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
